import request from 'app/api/request';
import ServerErrorHandler from 'app/ErrorHandler';
import { useQueryClient, useMutation, useQuery, UseQueryResult, UseMutationResult } from 'react-query';
import { UUID } from './types';
import { dispatchMessage, EventType } from 'app/useToasts';
import { i18n } from 'app/utils/i18n';

export const mandatoryQueryKeys = {
  concepts: 'MANDATORY_CONCEPTS',
  progress: 'MANDATORY_PROGRESS',
  status: 'MANDATORY_STATUS',
} as const;

export interface MandatoryConcept {
  id: UUID;
  name: string;
  labels: Record<string, Record<string, string>>;
}

export interface MandatoryProgress {
  total: number;
  tagged: number;
  dismissed: number;
  addressed: number;
  percentage: number;
}

export interface MandatoryDismissal {
  message: string;
  dismissedBy: number;
  dismissedAt: string;
}

export interface MandatoryStatusEntry {
  conceptId: UUID;
  name: string;
  labels: Record<string, Record<string, string>>;
  tagged: boolean;
  dismissal: MandatoryDismissal | null;
}

export function useMandatoryConcepts(taxonomyId: UUID | undefined): UseQueryResult<MandatoryConcept[]> {
  return useQuery(
    [mandatoryQueryKeys.concepts, taxonomyId],
    async () => {
      const res = await request.get(`/gaby/mandatory-tags/taxonomy/${taxonomyId}/concepts`);
      return (res?.data as MandatoryConcept[]) || [];
    },
    {
      enabled: !!taxonomyId,
      onError: ServerErrorHandler,
    }
  );
}

export function useMandatoryProgress(documentSuperId: number | undefined, taxonomyId: UUID | undefined): UseQueryResult<MandatoryProgress> {
  return useQuery(
    [mandatoryQueryKeys.progress, documentSuperId, taxonomyId],
    async () => {
      const res = await request.get(`/gaby/mandatory-tags/document/${documentSuperId}/progress?taxonomyId=${taxonomyId}`);
      return res?.data as MandatoryProgress;
    },
    {
      enabled: !!documentSuperId && !!taxonomyId,
      onError: ServerErrorHandler,
    }
  );
}

export function useMandatoryStatus(documentSuperId: number | undefined, taxonomyId: UUID | undefined): UseQueryResult<MandatoryStatusEntry[]> {
  return useQuery(
    [mandatoryQueryKeys.status, documentSuperId, taxonomyId],
    async () => {
      const res = await request.get(`/gaby/mandatory-tags/document/${documentSuperId}/status?taxonomyId=${taxonomyId}`);
      return (res?.data as MandatoryStatusEntry[]) || [];
    },
    {
      enabled: !!documentSuperId && !!taxonomyId,
      onError: ServerErrorHandler,
    }
  );
}

export interface DismissMandatoryInput {
  documentSuperId: number;
  conceptId: UUID;
  message: string;
}

export function useDismissMandatoryConcept(): UseMutationResult<unknown, unknown, DismissMandatoryInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ documentSuperId, conceptId, message }: DismissMandatoryInput) => {
      const res = await request.post(`/gaby/mandatory-tags/document/${documentSuperId}/dismissals`, { conceptId, message });
      return res?.data;
    },
    {
      onError: (error: any) => {
        let errorMessage = i18n('tag.mandatory.dismiss.error');
        try {
          const responseBody = JSON.parse(error.request?.responseText);
          if (responseBody?.error) errorMessage = i18n(responseBody.error);
        } catch {}
        dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: errorMessage, removeTime: 4000 });
      },
      onSuccess: (_data, variables) => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n('tag.mandatory.dismiss.success'),
          removeTime: 4000,
        });
        queryClient.invalidateQueries([mandatoryQueryKeys.progress, variables.documentSuperId]);
        queryClient.invalidateQueries([mandatoryQueryKeys.status, variables.documentSuperId]);
      },
    }
  );
}

export interface UndismissMandatoryInput {
  documentSuperId: number;
  conceptId: UUID;
}

export function useUndismissMandatoryConcept(): UseMutationResult<unknown, unknown, UndismissMandatoryInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ documentSuperId, conceptId }: UndismissMandatoryInput) => {
      const res = await request.del(`/gaby/mandatory-tags/document/${documentSuperId}/dismissals/${conceptId}`);
      return res?.data;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([mandatoryQueryKeys.progress, variables.documentSuperId]);
        queryClient.invalidateQueries([mandatoryQueryKeys.status, variables.documentSuperId]);
      },
    }
  );
}
